/**
 * Normalisers for the CSV import (spec 14.1, section 17): what an Access or Excel export
 * actually contains, turned into what the validators accept. Research finding 3 named the
 * three dirty cases and every function here answers one of them:
 *
 * - dates typed as text in the Indonesian reading order ("12/05/1987", "12-05-1987",
 *   "12 Mei 1987", "Mei 1987", "1987"), Access timestamps ("12/05/1987 0:00:00"), ISO
 *   dates, Excel day serials ("31909"), two-digit years as Excel itself would read them;
 * - names with the baptismal name in the same cell ("Kambu, Yohanes Paulus" surname first,
 *   "Yohanes Yufuwai (Yosef)" with the baptismal name in brackets), typed in capitals;
 * - lingkungan spelled three ways ("Lingk. St. Maria", "Santa Maria", "LINGKUNGAN ST MARIA")
 *   matched by an alias key against the parish's own territory list.
 *
 * Plus the code lists: Dukcapil pendidikan, pekerjaan, SHDK and status kawin labels or
 * codes, and the everyday words clerks write instead ("SMA", "IRT", "Petani", "Janda").
 *
 * Every reader returns a `Hasil`: `kosong` for an empty cell, `nilai` for a reading, `gagal`
 * for text that is there but cannot be read. The caller decides whether a failure is an
 * error (a date) or a note (an unknown occupation, dropped and reported).
 */
import { formatPartialDate, isPartialDate, parsePartialDateInput } from '../dates/partial'
import {
  type DaftarKode,
  HUBUNGAN_DARI_SHDK,
  PEKERJAAN,
  PENDIDIKAN,
  SHDK,
  STATUS_KAWIN,
} from '../lookups/index'
import { bacaNomorKkSebaris } from '../nomorKk'
import type { HUBUNGAN, STATUS_GEREJA, STATUS_HIDUP, STATUS_PERKAWINAN } from '../schema/contract'
import { ANAK_KE_MAKS } from '../validators/kolom'

export type Hasil<T> = { jenis: 'kosong' } | { jenis: 'nilai'; nilai: T } | { jenis: 'gagal' }

const KOSONG: Hasil<never> = { jenis: 'kosong' }
const GAGAL: Hasil<never> = { jenis: 'gagal' }
const nilai = <T>(v: T): Hasil<T> => ({ jenis: 'nilai', nilai: v })

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

/**
 * Cell values that mean "nothing": dashes, Excel and Access null markers, and the unknown
 * markers old registries write ("tidak tahu", "tdk tahu"), which read as empty rather than unreadable,
 * so a person whose birth date is not known is still imported (final review 3, Minor 7).
 */
const KOSONG_TEKS = new Set([
  '',
  '-',
  '–',
  '—',
  'null',
  'n/a',
  '#n/a',
  'na',
  'nihil',
  '(kosong)',
  'tidak tahu',
  'tdk tahu',
  'tidak diketahui',
  'tdk diketahui',
  'belum diketahui',
])

/** Trim, collapse whitespace; null for an empty or placeholder cell. */
export function rapikanTeks(text: string | null | undefined): string | null {
  if (text === null || text === undefined) return null
  const t = text.replace(/\s+/g, ' ').trim()
  return KOSONG_TEKS.has(t.toLowerCase()) ? null : t
}

/**
 * The comparison key of a name or a label: diacritics off, lower case, one space between
 * words, no punctuation. "St. Maria" and "ST MARIA" share a key.
 */
export function kunciTeks(text: string): string {
  return text
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim()
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

const BULAN: Readonly<Record<string, number>> = {
  januari: 1,
  jan: 1,
  january: 1,
  februari: 2,
  pebruari: 2,
  feb: 2,
  peb: 2,
  february: 2,
  maret: 3,
  mar: 3,
  march: 3,
  april: 4,
  apr: 4,
  mei: 5,
  may: 5,
  juni: 6,
  jun: 6,
  june: 6,
  juli: 7,
  jul: 7,
  july: 7,
  agustus: 8,
  agu: 8,
  agt: 8,
  ags: 8,
  aug: 8,
  august: 8,
  september: 9,
  sep: 9,
  sept: 9,
  oktober: 10,
  okt: 10,
  oct: 10,
  october: 10,
  november: 11,
  nopember: 11,
  nov: 11,
  nop: 11,
  desember: 12,
  des: 12,
  dec: 12,
  december: 12,
}

/**
 * The spreadsheet and form tools' "no date": 1 January of year 1 (release 19, the parish data
 * sheet), day-first or ISO. It reads as empty, never as a failure.
 */
const TANPA_TANGGAL = /^(?:0?1[\s\-/.]0?1[\s\-/.]0001|0001[\s\-/.]0?1[\s\-/.]0?1)$/

/** Excel's serial day 0 (1899-12-30) as a UTC instant. */
const EXCEL_EPOCH_MS = Date.UTC(1899, 11, 30)
const SERIAL_MIN = 1000
const SERIAL_MAX = 80000

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

function tanggalDari(tahun: number, bulan?: number, hari?: number): string | null {
  let text = String(tahun)
  if (bulan !== undefined) text += `-${pad2(bulan)}`
  if (hari !== undefined) text += `-${pad2(hari)}`
  return isPartialDate(text) ? text : null
}

/** Excel's own rule for two-digit years: 00 to 29 are 2000s, 30 to 99 are 1900s. */
export function tahunDuaDigit(yy: number): number {
  return yy < 30 ? 2000 + yy : 1900 + yy
}

/** An Excel day serial ("31909") to canonical text; null outside the plausible range. */
export function tanggalDariSerialExcel(serial: number): string | null {
  if (!Number.isInteger(serial) || serial < SERIAL_MIN || serial > SERIAL_MAX) return null
  const d = new Date(EXCEL_EPOCH_MS + serial * 86_400_000)
  return tanggalDari(d.getUTCFullYear(), d.getUTCMonth() + 1, d.getUTCDate())
}

/**
 * Read a date cell into the canonical partial form (`YYYY`, `YYYY-MM`, `YYYY-MM-DD`).
 * Day-first throughout; a month-first reading is taken only when the day-first one is
 * impossible ("05/31/1987").
 */
export function bacaTanggal(text: string | null | undefined): Hasil<string> {
  const raw = rapikanTeks(text)
  if (raw === null) return KOSONG
  // Access and ISO timestamps: drop the time of day.
  let t = raw
    .replace(
      /[T\s]\d{1,2}[:.]\d{2}(?:[:.]\d{2})?(?:\.\d+)?\s*(?:z|[+-]\d{2}:?\d{2}|am|pm|wit|wita|wib)?$/i,
      '',
    )
    .trim()
  t = t
    .replace(/^tanggal\s+|^tgl\.?\s+/i, '')
    .replace(/,/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (TANPA_TANGGAL.test(t)) return KOSONG

  // A bare number: a year, or an Excel serial.
  const angka = /^(\d+)(?:[.,]0+)?$/.exec(t)
  if (angka !== null) {
    const n = Number(angka[1])
    if (angka[1]?.length === 4 && n >= 1000 && n <= 2200) return nilai(String(n))
    const serial = tanggalDariSerialExcel(n)
    return serial === null ? GAGAL : nilai(serial)
  }

  // Already canonical, or the typed day-first forms the app's own fields accept.
  const typed = parsePartialDateInput(t)
  if (typed !== null) return nilai(typed)

  const parts = t.split(/[\s\-/.]+/).filter((p) => p.length > 0)
  const numeric = parts.every((p) => /^\d+$/.test(p))
  if (numeric && parts.length === 3) {
    const [a, b, c] = parts as [string, string, string]
    // yyyy-mm-dd with single digits, or dd-mm-yy, or mm-dd-yyyy.
    if (a.length === 4) return hasilTanggal(Number(a), Number(b), Number(c))
    const tahun = c.length === 4 ? Number(c) : c.length === 2 ? tahunDuaDigit(Number(c)) : null
    if (tahun === null) return GAGAL
    const dayFirst = tanggalDari(tahun, Number(b), Number(a))
    if (dayFirst !== null) return nilai(dayFirst)
    const monthFirst = tanggalDari(tahun, Number(a), Number(b))
    return monthFirst === null ? GAGAL : nilai(monthFirst)
  }
  if (numeric && parts.length === 2) {
    // Month and year only: the year must be written in full ("12-05" is not a date).
    const [a, b] = parts as [string, string]
    if (a.length === 4) return hasilTanggal(Number(a), Number(b))
    if (b.length !== 4) return GAGAL
    return hasilTanggal(Number(b), Number(a))
  }

  // Month names: "12 Mei 1987", "Mei 1987", "12-Mei-87", "Mei, 1987".
  const named = parts.map((p) => ({ p, bulan: BULAN[p.toLowerCase()] }))
  const bulanIdx = named.findIndex((x) => x.bulan !== undefined)
  if (bulanIdx === -1) return GAGAL
  const bulan = named[bulanIdx]?.bulan as number
  const rest = named.filter((_, i) => i !== bulanIdx).map((x) => x.p)
  if (rest.length === 1 && rest[0] !== undefined && /^\d+$/.test(rest[0])) {
    const y = rest[0]
    const tahun = y.length === 4 ? Number(y) : y.length === 2 ? tahunDuaDigit(Number(y)) : null
    return tahun === null ? GAGAL : hasilTanggal(tahun, bulan)
  }
  if (rest.length === 2 && rest.every((p) => /^\d+$/.test(p))) {
    const [d, y] = rest as [string, string]
    if (d.length > 2) return GAGAL
    const tahun = y.length === 4 ? Number(y) : y.length === 2 ? tahunDuaDigit(Number(y)) : null
    return tahun === null ? GAGAL : hasilTanggal(tahun, bulan, Number(d))
  }
  return GAGAL
}

function hasilTanggal(tahun: number, bulan?: number, hari?: number): Hasil<string> {
  const text = tanggalDari(tahun, bulan, hari)
  return text === null ? GAGAL : nilai(text)
}

/** Canonical text for a Date's UTC calendar day (used by tests and the template notes). */
export function tanggalKanonik(tahun: number, bulan: number, hari: number): string {
  return formatPartialDate({ tahun, bulan, hari })
}

// ---------------------------------------------------------------------------
// Names
// ---------------------------------------------------------------------------

/** Words kept lower case inside a capitalised name. */
const PARTIKEL = new Set(['bin', 'binti', 'van', 'de', 'der', 'da', 'dos', 'di', 'do', 'y', 'e'])

function kapital(word: string): string {
  return word
    .split(/([-'’])/)
    .map((piece) =>
      /^[-'’]$/.test(piece) || piece.length === 0
        ? piece
        : piece.charAt(0).toLocaleUpperCase('id-ID') + piece.slice(1).toLocaleLowerCase('id-ID'),
    )
    .join('')
}

/**
 * Tidy a name: whitespace and stray punctuation, and a cell typed in one case turned into title
 * case, all capitals ("YOHANES YUFUWAI") or all small letters ("agnes owaw", the test sweep of
 * 2026-10-04). Mixed case is kept as typed.
 */
export function rapikanNama(text: string | null | undefined): string | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  const cleaned = t.replace(/^[,;.\s]+|[,;\s]+$/g, '').replace(/\s*,\s*/g, ', ')
  if (cleaned === '') return null
  const campuran = /\p{Ll}/u.test(cleaned) && /\p{Lu}/u.test(cleaned)
  if (campuran) return cleaned
  return cleaned
    .split(' ')
    .map((word, i) =>
      i > 0 && PARTIKEL.has(word.toLowerCase()) ? word.toLowerCase() : kapital(word),
    )
    .join(' ')
}

export interface NamaTerpisah {
  namaLengkap: string
  /** From brackets ("Yohanes Yufuwai (Yosef)") or a leading "Yosef / Yohanes Yufuwai". */
  namaBaptis: string | null
}

/**
 * One name cell as exports write it: "Kambu, Yohanes Paulus" (surname first) becomes
 * "Yohanes Paulus Kambu"; a bracketed or slash-separated baptismal name is split off.
 * Returns null for an empty cell.
 */
export function pisahNama(text: string | null | undefined): NamaTerpisah | null {
  let t = rapikanTeks(text)
  if (t === null) return null
  let namaBaptis: string | null = null
  const kurung = /\(([^)]+)\)/.exec(t)
  if (kurung !== null && kurung[1] !== undefined) {
    namaBaptis = rapikanNama(kurung[1])
    t = t.replace(kurung[0], ' ').replace(/\s+/g, ' ').trim()
  } else {
    const garis = /^([^/|]+?)\s*[/|]\s*(.+)$/.exec(t)
    if (garis !== null && garis[1] !== undefined && garis[2] !== undefined) {
      namaBaptis = rapikanNama(garis[1])
      t = garis[2]
    }
  }
  // "Kambu, Yohanes Paulus": one comma, surname before it.
  const koma = t.split(',').map((p) => p.trim())
  if (koma.length === 2 && koma[0] !== '' && koma[1] !== '') t = `${koma[1]} ${koma[0]}`
  const namaLengkap = rapikanNama(t)
  if (namaLengkap === null) return null
  return { namaLengkap, namaBaptis }
}

// ---------------------------------------------------------------------------
// Gender, church status, life status
// ---------------------------------------------------------------------------

function pilih<T extends string>(
  text: string | null | undefined,
  tabel: Readonly<Record<string, T>>,
): Hasil<T> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const found = tabel[kunciTeks(t)]
  return found === undefined ? GAGAL : nilai(found)
}

const JENIS_KELAMIN_ALIAS: Readonly<Record<string, 'L' | 'P'>> = {
  l: 'L',
  lk: 'L',
  laki: 'L',
  'laki laki': 'L',
  lelaki: 'L',
  pria: 'L',
  male: 'L',
  m: 'L',
  '1': 'L',
  p: 'P',
  pr: 'P',
  perempuan: 'P',
  wanita: 'P',
  female: 'P',
  f: 'P',
  w: 'P',
  '2': 'P',
}

export function bacaJenisKelamin(text: string | null | undefined): Hasil<'L' | 'P'> {
  return pilih(text, JENIS_KELAMIN_ALIAS)
}

type StatusGereja = (typeof STATUS_GEREJA)[number]
const STATUS_GEREJA_ALIAS: Readonly<Record<string, StatusGereja>> = {
  katolik: 'katolik',
  katholik: 'katolik',
  catholic: 'katolik',
  kat: 'katolik',
  k: 'katolik',
  rk: 'katolik',
  'roma katolik': 'katolik',
  'kristen katolik': 'katolik',
  'katolik roma': 'katolik',
  katekumen: 'katekumen',
  katekumin: 'katekumen',
  'calon baptis': 'katekumen',
  'calon katolik': 'katekumen',
  'belum baptis': 'katekumen',
  'belum dibaptis': 'katekumen',
  simpatisan: 'katekumen',
  'non katolik': 'non_katolik',
  'bukan katolik': 'non_katolik',
  'bukan katolik anggota keluarga': 'non_katolik',
  kristen: 'non_katolik',
  protestan: 'non_katolik',
  'kristen protestan': 'non_katolik',
  islam: 'non_katolik',
  muslim: 'non_katolik',
  hindu: 'non_katolik',
  budha: 'non_katolik',
  buddha: 'non_katolik',
  lain: 'non_katolik',
  lainnya: 'non_katolik',
  'agama lain': 'non_katolik',
}

export function bacaStatusGereja(text: string | null | undefined): Hasil<StatusGereja> {
  return pilih(text, STATUS_GEREJA_ALIAS)
}

type StatusHidup = (typeof STATUS_HIDUP)[number]
const STATUS_HIDUP_ALIAS: Readonly<Record<string, StatusHidup>> = {
  hidup: 'hidup',
  'masih hidup': 'hidup',
  h: 'hidup',
  ya: 'hidup',
  meninggal: 'meninggal',
  'meninggal dunia': 'meninggal',
  wafat: 'meninggal',
  mati: 'meninggal',
  almarhum: 'meninggal',
  almarhumah: 'meninggal',
  alm: 'meninggal',
  almh: 'meninggal',
  m: 'meninggal',
  '†': 'meninggal',
}

export function bacaStatusHidup(text: string | null | undefined): Hasil<StatusHidup> {
  return pilih(text, STATUS_HIDUP_ALIAS)
}

const YA_TIDAK_ALIAS: Readonly<Record<string, 'ya' | 'tidak'>> = {
  ya: 'ya',
  y: 'ya',
  iya: 'ya',
  yes: 'ya',
  benar: 'ya',
  betul: 'ya',
  '1': 'ya',
  x: 'ya',
  v: 'ya',
  tidak: 'tidak',
  tdk: 'tidak',
  t: 'tidak',
  no: 'tidak',
  n: 'tidak',
  '0': 'tidak',
}

/**
 * A yes-or-no cell: ya, iya, y, benar, 1, x or a tick; tidak, tdk, 0. Never "bukan": under a
 * header that names a negation ("Bukan umat paroki ini") it may well mean yes, so it reads as
 * neither and the caller says so.
 */
export function bacaYaTidak(text: string | null | undefined): Hasil<'ya' | 'tidak'> {
  const t = rapikanTeks(text)
  if (t !== null && /^[✓✔☑]$/u.test(t)) return nilai('ya')
  return pilih(text, YA_TIDAK_ALIAS)
}

// ---------------------------------------------------------------------------
// Code lists (Dukcapil labels or codes, plus everyday words)
// ---------------------------------------------------------------------------

/**
 * Match a cell against a code list: the code itself, the display label, the official
 * spelling, then the alias table. Returns the code.
 */
export function bacaKode(
  text: string | null | undefined,
  daftar: DaftarKode,
  alias: Readonly<Record<string, string>> = {},
): Hasil<string> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const key = kunciTeks(t)
  if (key === '') return KOSONG
  const byCode = daftar.entri.find((e) => e.kode.toLowerCase() === t.toLowerCase())
  if (byCode !== undefined) return nilai(byCode.kode)
  const byLabel = daftar.entri.find(
    (e) =>
      kunciTeks(e.label) === key || (e.labelResmi !== undefined && kunciTeks(e.labelResmi) === key),
  )
  if (byLabel !== undefined) return nilai(byLabel.kode)
  const viaAlias = alias[key]
  return viaAlias === undefined ? GAGAL : nilai(viaAlias)
}

export const PENDIDIKAN_ALIAS: Readonly<Record<string, string>> = {
  'tidak sekolah': '1',
  'belum sekolah': '1',
  'tidak belum sekolah': '1',
  'tidak pernah sekolah': '1',
  'belum tidak sekolah': '1',
  tk: '1',
  paud: '1',
  'paud tk': '1',
  'belum tk': '1',
  'tidak ada': '1',
  'belum tamat sd': '2',
  'belum tamat sd sederajat': '2',
  'sd belum tamat': '2',
  'tidak tamat sd': '2',
  'putus sd': '2',
  'masih sd': '2',
  sd: '3',
  'tamat sd': '3',
  'sd sederajat': '3',
  'sekolah dasar': '3',
  mi: '3',
  sdlb: '3',
  smp: '4',
  sltp: '4',
  'tamat smp': '4',
  'smp sederajat': '4',
  mts: '4',
  'sekolah menengah pertama': '4',
  smplb: '4',
  sma: '5',
  slta: '5',
  smu: '5',
  smk: '5',
  ma: '5',
  'tamat sma': '5',
  'sma sederajat': '5',
  'sekolah menengah atas': '5',
  'sekolah menengah kejuruan': '5',
  stm: '5',
  smea: '5',
  spg: '5',
  smalb: '5',
  d1: '6',
  d2: '6',
  'd i': '6',
  'd ii': '6',
  'diploma 1': '6',
  'diploma 2': '6',
  'diploma i': '6',
  'diploma ii': '6',
  'diploma i ii': '6',
  d3: '7',
  'd iii': '7',
  'diploma 3': '7',
  'diploma iii': '7',
  akademi: '7',
  'sarjana muda': '7',
  s1: '8',
  's 1': '8',
  d4: '8',
  'd iv': '8',
  'diploma 4': '8',
  'diploma iv': '8',
  sarjana: '8',
  'strata 1': '8',
  'strata i': '8',
  'strata satu': '8',
  s2: '9',
  's 2': '9',
  magister: '9',
  master: '9',
  'strata 2': '9',
  'strata ii': '9',
  pascasarjana: '9',
  s3: '10',
  's 3': '10',
  doktor: '10',
  doctor: '10',
  'strata 3': '10',
  'strata iii': '10',
}

export const PEKERJAAN_ALIAS: Readonly<Record<string, string>> = {
  'tidak bekerja': '1',
  'belum bekerja': '1',
  'belum tidak bekerja': '1',
  'tidak ada': '1',
  pengangguran: '1',
  'ibu rumah tangga': '2',
  irt: '2',
  urt: '2',
  'rumah tangga': '2',
  'mengurus rumah tangga': '2',
  pelajar: '3',
  mahasiswa: '3',
  mahasiswi: '3',
  siswa: '3',
  siswi: '3',
  murid: '3',
  sekolah: '3',
  'masih sekolah': '3',
  'pelajar mahasiswa': '3',
  pensiun: '4',
  pensiunan: '4',
  'pensiunan pns': '4',
  purnawirawan: '4',
  pns: '5',
  asn: '5',
  'pegawai negeri': '5',
  'pegawai negeri sipil': '5',
  'aparatur sipil negara': '5',
  tni: '6',
  tentara: '6',
  prajurit: '6',
  polri: '7',
  polisi: '7',
  kepolisian: '7',
  perdagangan: '8',
  dagang: '8',
  petani: '9',
  pekebun: '9',
  tani: '9',
  bertani: '9',
  berkebun: '9',
  peladang: '9',
  'petani pekebun': '9',
  peternak: '10',
  ternak: '10',
  nelayan: '11',
  perikanan: '11',
  'penangkap ikan': '11',
  'nelayan perikanan': '11',
  ojek: '14',
  'tukang ojek': '14',
  ojol: '14',
  'tukang perahu': '14',
  motoris: '14',
  transportasi: '14',
  karyawan: '15',
  'karyawan swasta': '15',
  swasta: '15',
  'pegawai swasta': '15',
  pegawai: '15',
  bumn: '16',
  'karyawan bumn': '16',
  'pegawai bumn': '16',
  bumd: '17',
  'karyawan bumd': '17',
  honorer: '18',
  honor: '18',
  'tenaga honorer': '18',
  kontrak: '18',
  'pegawai kontrak': '18',
  p3k: '18',
  pppk: '18',
  buruh: '19',
  'buruh harian': '19',
  'buruh lepas': '19',
  'buruh harian lepas': '19',
  'buruh bangunan': '19',
  'buruh tani': '20',
  'buruh kebun': '20',
  'buruh perkebunan': '20',
  'buruh nelayan': '21',
  'buruh perikanan': '21',
  pembantu: '23',
  prt: '23',
  art: '23',
  'pembantu rumah tangga': '23',
  'tukang kayu': '27',
  penjahit: '30',
  'tukang jahit': '30',
  jahit: '30',
  mekanik: '35',
  montir: '35',
  bengkel: '35',
  seniman: '36',
  pengukir: '36',
  pemahat: '36',
  'tukang ukir': '36',
  pemusik: '36',
  penari: '36',
  pelukis: '36',
  pendeta: '42',
  pastor: '43',
  imam: '43',
  pater: '43',
  'juru masak': '46',
  koki: '46',
  'tukang masak': '46',
  dosen: '64',
  guru: '65',
  pengajar: '65',
  dokter: '72',
  bidan: '73',
  perawat: '74',
  mantri: '74',
  pelaut: '79',
  abk: '79',
  'awak kapal': '79',
  sopir: '81',
  supir: '81',
  driver: '81',
  pengemudi: '81',
  pedagang: '84',
  penjual: '84',
  jualan: '84',
  kios: '84',
  warung: '84',
  'pedagang kecil': '84',
  'perangkat desa': '85',
  'perangkat kampung': '85',
  'aparat kampung': '85',
  'aparat desa': '85',
  'sekretaris kampung': '85',
  'sekretaris desa': '85',
  sekdes: '85',
  'kepala desa': '86',
  'kepala kampung': '86',
  kades: '86',
  lurah: '86',
  biarawati: '87',
  suster: '87',
  sr: '87',
  sister: '87',
  wiraswasta: '88',
  wirausaha: '88',
  pengusaha: '88',
  'usaha sendiri': '88',
  manajer: '93',
  manager: '93',
  'tata usaha': '94',
  tu: '94',
  'staf tu': '94',
  administrasi: '94',
  'staf administrasi': '94',
  sekretaris: '94',
  operator: '95',
  teknisi: '97',
  lainnya: '99',
  'lain lain': '99',
  lain: '99',
  dll: '99',
  gembala: '100',
  uskup: '101',
  biarawan: '102',
  katekis: 'G1',
  frater: 'G2',
  seminaris: 'G2',
  fr: 'G2',
  'calon imam': 'G2',
  bruder: 'G3',
  br: 'G3',
}

/**
 * Two-letter words that are a level only as the whole cell: at the start of a longer one they are
 * as often something else ("MA" a name, "PT" a company).
 */
const AWALAN_TERLALU_PENDEK: ReadonlySet<string> = new Set(['ma', 'mi', 'pt', 'kb'])

/**
 * A cell read whole, else by its first three, two or one words (release 19: the parish's form
 * asks for "the school, with its level": "SMA Negeri 1 Agats", "S1 Teknik Sipil"). Only the start
 * is read, so "Belum tamat SMP" is never SMP; a number at the start is never a code.
 */
export function bacaDenganAwalan<T>(
  text: string | null | undefined,
  baca: (t: string) => Hasil<T>,
): Hasil<T> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const utuh = baca(t)
  if (utuh.jenis !== 'gagal') return utuh
  const awal = bacaAwalan(t, baca)
  return awal === null ? GAGAL : nilai(awal)
}

/** The reading of a cell's first words, or null when none reads (the whole cell is not tried). */
export function bacaAwalan<T>(t: string, baca: (t: string) => Hasil<T>): T | null {
  const kata = t.split(' ')
  for (let n = Math.min(3, kata.length - 1); n >= 1; n -= 1) {
    const kepala = kata.slice(0, n).join(' ')
    if (/^\d+$/.test(kepala) || AWALAN_TERLALU_PENDEK.has(kunciTeks(kepala))) continue
    const h = baca(kepala)
    if (h.jenis === 'nilai') return h.nilai
  }
  return null
}

/** The highest level completed: a Dukcapil code, from the whole cell or its start. */
export function bacaPendidikan(text: string | null | undefined): Hasil<string> {
  return bacaDenganAwalan(text, (t) => bacaKode(t, PENDIDIKAN, PENDIDIKAN_ALIAS))
}

export function bacaPekerjaan(text: string | null | undefined): Hasil<string> {
  return bacaKode(text, PEKERJAAN, PEKERJAAN_ALIAS)
}

// ---------------------------------------------------------------------------
// Household role: SHDK codes and labels, then the words clerks write
// ---------------------------------------------------------------------------

type Hubungan = (typeof HUBUNGAN)[number]
const HUBUNGAN_ALIAS: Readonly<Record<string, Hubungan>> = {
  'kepala keluarga': 'kepala',
  kepala: 'kepala',
  kk: 'kepala',
  'kep kel': 'kepala',
  'kepala rumah tangga': 'kepala',
  krt: 'kepala',
  suami: 'suami',
  istri: 'istri',
  isteri: 'istri',
  anak: 'anak',
  'anak kandung': 'anak',
  'anak tiri': 'anak',
  'anak angkat': 'anak',
  putra: 'anak',
  putri: 'anak',
  'orang tua': 'orang_tua',
  orangtua: 'orang_tua',
  'ayah kandung': 'orang_tua',
  'ibu kandung': 'orang_tua',
  ayah: 'orang_tua',
  bapak: 'orang_tua',
  ibu: 'orang_tua',
  'famili lain': 'famili_lain',
  famili: 'famili_lain',
  'keluarga lain': 'famili_lain',
  lainnya: 'famili_lain',
  lain: 'famili_lain',
  saudara: 'famili_lain',
  adik: 'famili_lain',
  kakak: 'famili_lain',
  keponakan: 'famili_lain',
  kemenakan: 'famili_lain',
  cucu: 'famili_lain',
  menantu: 'famili_lain',
  mertua: 'famili_lain',
  pembantu: 'famili_lain',
  ipar: 'famili_lain',
  sepupu: 'famili_lain',
  paman: 'famili_lain',
  bibi: 'famili_lain',
}

/**
 * The place among the children a "Status Hubungan Keluarga" cell gives (release 19, the parish
 * data sheet): "Anak 1", "Anak ke-3", "Anak Kandung 2"; null for any other cell.
 */
export function anakKeDari(text: string | null | undefined): number | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  const m = /^anak(?: (?:kandung|tiri|angkat))?(?: ke)? (\d{1,2})$/.exec(kunciTeks(t))
  const n = m?.[1] === undefined ? null : Number(m[1])
  return n !== null && n >= 1 && n <= ANAK_KE_MAKS ? n : null
}

export function bacaHubungan(text: string | null | undefined): Hasil<Hubungan> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  // "Anak 2": a child, the second (`anakKeDari`).
  if (anakKeDari(t) !== null) return nilai('anak')
  const shdk = bacaKode(t, SHDK)
  if (shdk.jenis === 'nilai') {
    const church = HUBUNGAN_DARI_SHDK[shdk.nilai]
    if (church !== undefined) return nilai(church)
  }
  return pilih(t, HUBUNGAN_ALIAS)
}

// ---------------------------------------------------------------------------
// Marital status: Dukcapil status kawin codes and labels, then the Church words
// ---------------------------------------------------------------------------

type StatusPerkawinan = (typeof STATUS_PERKAWINAN)[number]

/**
 * Dukcapil codes to the Church enum. A civil registration in Indonesia follows a
 * religious marriage, so "Kawin Tercatat" is read as a Church marriage and "Kawin Belum
 * Tercatat" as a marriage not yet blessed; the preview shows the reading so the desk can
 * correct it.
 */
const STATUS_KAWIN_KE_GEREJA: Readonly<Record<string, StatusPerkawinan>> = {
  '1': 'belum_menikah',
  '2': 'menikah_gereja',
  '3': 'menikah_belum_gereja',
  '4': 'cerai',
  '5': 'cerai',
  '6': 'janda_duda',
}

const STATUS_PERKAWINAN_ALIAS: Readonly<Record<string, StatusPerkawinan>> = {
  'belum menikah': 'belum_menikah',
  'belum kawin': 'belum_menikah',
  'belum nikah': 'belum_menikah',
  'tidak kawin': 'belum_menikah',
  lajang: 'belum_menikah',
  single: 'belum_menikah',
  bujang: 'belum_menikah',
  bk: 'belum_menikah',
  kawin: 'menikah_gereja',
  menikah: 'menikah_gereja',
  nikah: 'menikah_gereja',
  'sudah menikah': 'menikah_gereja',
  'sudah kawin': 'menikah_gereja',
  'menikah gereja': 'menikah_gereja',
  'menikah di gereja': 'menikah_gereja',
  'nikah gereja': 'menikah_gereja',
  'kawin gereja': 'menikah_gereja',
  'menikah katolik': 'menikah_gereja',
  'kawin katolik': 'menikah_gereja',
  'perkawinan gereja': 'menikah_gereja',
  sah: 'menikah_gereja',
  'menikah belum gereja': 'menikah_belum_gereja',
  'menikah belum di gereja': 'menikah_belum_gereja',
  'belum gereja': 'menikah_belum_gereja',
  'belum sah': 'menikah_belum_gereja',
  'nikah adat': 'menikah_belum_gereja',
  'kawin adat': 'menikah_belum_gereja',
  'menikah adat': 'menikah_belum_gereja',
  'kawin sipil': 'menikah_belum_gereja',
  'nikah sipil': 'menikah_belum_gereja',
  'menikah sipil': 'menikah_belum_gereja',
  'kumpul kebo': 'menikah_belum_gereja',
  cerai: 'cerai',
  'cerai hidup': 'cerai',
  pisah: 'cerai',
  berpisah: 'cerai',
  janda: 'janda_duda',
  duda: 'janda_duda',
  'janda duda': 'janda_duda',
  'cerai mati': 'janda_duda',
  religius: 'religius',
  biarawan: 'religius',
  biarawati: 'religius',
  suster: 'religius',
  bruder: 'religius',
  frater: 'religius',
  'hidup bakti': 'religius',
  kaul: 'religius',
  imam: 'imam',
  pastor: 'imam',
  pater: 'imam',
}

export function bacaStatusPerkawinan(text: string | null | undefined): Hasil<StatusPerkawinan> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const dukcapil = bacaKode(t, STATUS_KAWIN)
  if (dukcapil.jenis === 'nilai') {
    const church = STATUS_KAWIN_KE_GEREJA[dukcapil.nilai]
    if (church !== undefined) return nilai(church)
  }
  return pilih(t, STATUS_PERKAWINAN_ALIAS)
}

// ---------------------------------------------------------------------------
// Numbers as text: NIK, government KK, phone, the Catholic KK number
// ---------------------------------------------------------------------------

/** Digits only; a scientific-notation cell ("9.1E+15") has lost digits and fails. */
export function bacaDigit(text: string | null | undefined, panjang: number): Hasil<string> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  if (/e\+?\d/i.test(t)) return GAGAL
  const digits = t
    .replace(/^'+/, '')
    .replace(/[.,]0+$/, '')
    .replace(/\D+/g, '')
  if (digits === '') return GAGAL
  return digits.length === panjang ? nilai(digits) : GAGAL
}

export function bacaNik(text: string | null | undefined): Hasil<string> {
  return bacaDigit(text, 16)
}

/**
 * A phone cell to the `08…` or `+62…` form the validator accepts: separators dropped,
 * "62…" given its plus, a leading zero Excel ate put back, only the first of several
 * numbers kept.
 */
export function bacaTelepon(text: string | null | undefined): Hasil<string> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const first = t.split(/[/,;]| dan | atau /i)[0] ?? ''
  let digits = first.replace(/^'+/, '').replace(/[\s\-.()]/g, '')
  if (digits === '') return GAGAL
  if (digits.startsWith('+')) digits = `+${digits.slice(1).replace(/\D/g, '')}`
  else digits = digits.replace(/\D/g, '')
  if (/^0062/.test(digits)) digits = `+62${digits.slice(4)}`
  else if (/^62\d{8,12}$/.test(digits)) digits = `+${digits}`
  else if (/^8\d{7,11}$/.test(digits)) digits = `0${digits}`
  return /^(08\d{7,11}|\+62\d{8,12})$/.test(digits) ? nilai(digits) : GAGAL
}

/** A KK cell as text, leading zeros kept, Excel's text mark and an Excel ".0" dropped. */
export function bacaNomorKk(text: string | null | undefined): string | null {
  const t = rapikanTeks(text)
  if (t === null) return null
  const cleaned = t.replace(/^'+/, '').replace(/^(\d+)[.,]0+$/, '$1')
  return cleaned === '' ? null : cleaned
}

/** What a "Nomor KK" cell holds (release 18, owner 2026-10-08). */
export type NomorKkBerkas =
  | { jenis: 'kosong' }
  /** 16 digits: the government (Disdukcapil) KK number. */
  | { jenis: 'pemerintah'; nomor: string }
  /** Anything else ("AP-001", "0231"): the family's Kode keluarga, as written. */
  | { jenis: 'kode'; kode: string }
  /** A number Excel kept in its exponent form ("9.10101E+15"): its digits are lost. */
  | { jenis: 'excel' }
  /** Eleven digits or more, but not 16: a government number mistyped, never a code. */
  | { jenis: 'salah' }

/** The separators a government number is written with in a sheet. */
const PEMISAH_KK_BERKAS = /[\s.\-/]+/g

/**
 * A "Nomor KK" cell: 16 digits, once spaces, dots, dashes and slashes go (a full-width digit read
 * as a digit), are the government number; a run of 11 or more digits otherwise is one mistyped;
 * any other value is the family's own code (`rapikanKodeKeluarga`'s spacing). Excel's text mark
 * and ".0" go first, and the note SATU's own downloads write after a parish number ("0231 (No.
 * KK Disdukcapil belum tersedia)", review M-3), so a download imports back.
 */
export function bacaKolomNomorKk(text: string | null | undefined): NomorKkBerkas {
  const sel = bacaNomorKk(text)
  const t = sel === null ? null : bacaNomorKk(bacaNomorKkSebaris(sel).nomor)
  if (t === null) return { jenis: 'kosong' }
  if (/^\d+(?:[.,]\d+)?e\+?\d+$/i.test(t)) return { jenis: 'excel' }
  const n = t.normalize('NFKC')
  if (/^[\d\s.\-/]+$/.test(n)) {
    const digit = n.replace(PEMISAH_KK_BERKAS, '')
    if (/^\d{16}$/.test(digit)) return { jenis: 'pemerintah', nomor: digit }
    if (digit.length >= 11) return { jenis: 'salah' }
  }
  return { jenis: 'kode', kode: t.replace(/\s+/g, ' ') }
}

// ---------------------------------------------------------------------------
// Territory: a lingkungan name against the parish's list
// ---------------------------------------------------------------------------

export interface UnitRujukan {
  id: string
  nama: string
}

const AWALAN_UNIT =
  /^(lingkungan|lingk|ling|lk|kombas|komunitas basis|kbg|kring|rukun|wilayah lingkungan)\s+/

/**
 * The alias key of a unit name: the "Lingkungan" prefix off, "Santo", "Santa", "Sta." and
 * "St" all read as "st", punctuation and case gone.
 */
export function kunciUnit(nama: string): string {
  let key = kunciTeks(nama)
  key = key.replace(AWALAN_UNIT, '')
  key = key
    .split(' ')
    .map((w) =>
      w === 'santo' || w === 'santa' || w === 'sta' || w === 'sto' || w === 's' ? 'st' : w,
    )
    .join(' ')
  return key.trim()
}

function tanpaSt(key: string): string {
  return key
    .split(' ')
    .filter((w) => w !== 'st')
    .join(' ')
}

/** The units a key names: exactly, else the same without the saint prefix. */
function unitBernama(key: string, daftar: readonly UnitRujukan[]): readonly UnitRujukan[] {
  const exact = daftar.filter((u) => kunciUnit(u.nama) === key)
  if (exact.length > 0) return exact
  const loose = tanpaSt(key)
  return loose === '' ? [] : daftar.filter((u) => tanpaSt(kunciUnit(u.nama)) === loose)
}

/**
 * Find the unit a cell names. Exact alias key first, then the same without the saint
 * prefix, then a single unit whose key contains the cell's key ("Maria" for "St. Maria")
 * when only one does. Null when nothing matches or several do.
 */
export function cariUnit(
  text: string | null | undefined,
  daftar: readonly UnitRujukan[],
): Hasil<UnitRujukan> {
  const t = rapikanTeks(text)
  if (t === null) return KOSONG
  const key = kunciUnit(t)
  if (key === '') return GAGAL
  const bernama = unitBernama(key, daftar)
  if (bernama.length === 1) return nilai(bernama[0] as UnitRujukan)
  if (bernama.length > 1) return GAGAL
  const loose = tanpaSt(key)
  if (loose !== '') {
    const contains = daftar.filter((u) => {
      const k = ` ${tanpaSt(kunciUnit(u.nama))} `
      return k.includes(` ${loose} `)
    })
    if (contains.length === 1) return nilai(contains[0] as UnitRujukan)
  }
  return GAGAL
}

/**
 * The units a cell names when it names several of one name (a lingkungan St. Yosef under the
 * centre and another under a stasi): what tells the clerk to pick it; [] otherwise.
 */
export function unitSenama(
  text: string | null | undefined,
  daftar: readonly UnitRujukan[],
): readonly UnitRujukan[] {
  const t = rapikanTeks(text)
  const key = t === null ? '' : kunciUnit(t)
  if (key === '') return []
  const bernama = unitBernama(key, daftar)
  return bernama.length > 1 ? bernama : []
}
